import { CartService } from './../../../../core/services/cart/cart.service';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  pairwise,
  startWith,
  Subscription,
} from 'rxjs';
import { Resident } from '../../../models/resident.model';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { InactiveResidentModalComponent } from '../../inactive-resident-modal/inactive-resident-modal.component';
import { PermissionsService } from '../../../../core/services/permissions/permissions.service';
import { PartnerPaymentGuardService } from '../../../../core/services/partner-payment-guard/partner-payment-guard.service';
import { ShopSelectionStore } from '../../../../core/state/shop-selection.store';

@Component({
  selector: 'app-product-counter',
  templateUrl: './product-counter.component.html',
  styleUrls: ['./product-counter.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, MatIconModule],
})
export class ProductCounterComponent implements OnInit {
  @Input() set counter(value) {
    this.formControl.setValue(value, { emitEvent: false });
  }
  @Input() buttonText: string = '';
  @Input() checkResident: boolean = true;
  @Input() canIncrease: (currentValue: number, nextValue: number) => boolean =
    () => true;
  /** Highest quantity this counter may reach; `null` leaves it unrestricted. */
  @Input() maxQuantity: number | null = null;
  formControl: FormControl = new FormControl(0);
  @Input() resident: Resident = null;
  @Input() listOfNeedsProduct: boolean = false;
  @Output() counterChange = new EventEmitter<{
    value: number;
    resident?: Resident;
  }>();
  isFocused: boolean = false;
  previousValue: string = '';

  /** At the limit the increase control goes inactive rather than silently refusing. */
  get isAtMaxQuantity(): boolean {
    return (
      this.maxQuantity !== null &&
      (Number(this.formControl.value) || 0) >= this.maxQuantity
    );
  }

  constructor(
    private cartService: CartService,
    private shopSelectionStore: ShopSelectionStore,
    private dialog: MatDialog,
    public permissionsService: PermissionsService,
    private partnerPaymentGuardService: PartnerPaymentGuardService
  ) {}

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        startWith(undefined),
        pairwise(),
        filter(([prev, curr]) => prev !== '' && curr !== '')
      )
      .subscribe(([prev, curr]) => {
        this.counterChange.emit({ value: curr, resident: this.resident });
      });
  }

  async increment() {
    if (!this.permissionsService.canAddProductsToCart()) return;
    const currentValue = Number(this.formControl.value) || 0;
    const nextValue = currentValue + 1;
    if (this.maxQuantity !== null && nextValue > this.maxQuantity) return;
    if (!this.canIncrease(currentValue, nextValue)) return;
    let resident = this.resident;
    if (!resident) {
      resident = await firstValueFrom(this.shopSelectionStore.resident$);
    }
    if (this.checkResident && resident?.active === 0) {
      this.dialog.open(InactiveResidentModalComponent, {
        data: { resident },
        width: '632px',
        height: 'auto',
        autoFocus: false,
      });
      return;
    }

    if (this.checkResident && (await this.cartService.shouldPreventShoping())) {
      this.cartService.setPreventShoping(true);
      return;
    }

    if (this.partnerPaymentGuardService.hasNoValidPaymentMethod(resident)) {
      return;
    }
    this.formControl.setValue(nextValue);
  }

  decrement() {
    if (!this.permissionsService.canAddProductsToCart()) return;
    const currentValue = Number(this.formControl.value) || 0;
    if (currentValue > 0) {
      this.formControl.setValue(currentValue - 1);
    }
  }

  onQuantityChange(event: Event) {
    if (!this.permissionsService.canAddProductsToCart()) return;
    const target = event.target as HTMLInputElement;
    if (Number.isInteger(parseInt(target.value, 10))) {
      const value = parseInt(target.value, 10);
      if (value >= 0) {
        const currentValue = this.committedQuantity(target.value);
        if (
          value > currentValue &&
          !this.isQuantityAllowed(currentValue, value)
        ) {
          this.settleOnHighestAllowed(currentValue, value);
          return;
        }
        this.previousValue = value.toString();
        this.formControl.setValue(value);
      }
    } else {
      this.formControl.setValue(0);
    }
  }

  /**
   * The quantity the counter last committed, which is what a typed value has to be judged
   * against. The form control alone cannot answer this while the customer is typing: the
   * reactive-forms value accessor writes the raw input into the control before this handler
   * runs, so by then the control already holds the very value that still needs checking.
   * When it does, the last committed quantity is the one kept in `previousValue`.
   */
  private committedQuantity(rawInput: string): number {
    const controlValue = this.formControl.value;

    if (`${controlValue}` === rawInput) {
      return Number(this.previousValue) || 0;
    }

    return Number(controlValue) || Number(this.previousValue) || 0;
  }

  /**
   * A refused quantity settles on the highest one the customer may actually have, not on the
   * digits typed so far: typing `101` against a limit of 100 belongs at 100, not back at the
   * `10` that was on the way there. The limit message has already been raised by the refusal.
   * A refusal that is not about the limit — an article not released for this area, an empty
   * cart — leaves the committed quantity untouched.
   */
  private settleOnHighestAllowed(
    currentValue: number,
    requested: number
  ): void {
    const capped =
      this.maxQuantity === null || this.maxQuantity <= currentValue
        ? currentValue
        : Math.min(requested, this.maxQuantity);
    const allowed =
      capped > currentValue && this.canIncrease(currentValue, capped)
        ? capped
        : currentValue;

    this.previousValue = allowed.toString();
    this.formControl.setValue(allowed, { emitEvent: allowed !== currentValue });
  }

  /**
   * `canIncrease` is asked first: it is the guard that surfaces the limit message, so it has
   * to run before `maxQuantity` — which stays as a backstop for counters bound to a limit
   * without a guard.
   */
  private isQuantityAllowed(currentValue: number, nextValue: number): boolean {
    if (!this.canIncrease(currentValue, nextValue)) {
      return false;
    }

    return this.maxQuantity === null || nextValue <= this.maxQuantity;
  }

  onFocus() {
    if (!this.permissionsService.canAddProductsToCart()) return;
    this.isFocused = true;
    this.previousValue = this.formControl.value;
    this.formControl.setValue('');
  }

  onBlur() {
    if (!this.permissionsService.canAddProductsToCart()) return;
    this.isFocused = false;
    // Restored as a number: the control feeds arithmetic, and a string would concatenate.
    this.formControl.setValue(Number(this.previousValue) || 0, {
      emitEvent: false,
    });
  }

  connextError(resident: Resident) {
    return (
      resident?.partnerResidentId &&
      !resident?.partnerAccountId &&
      resident.hasIBAN === 0
    );
  }
}
